import { useCallback } from 'react';
import { supabase } from '../../supabase/client';
import type { Order } from '../../types';

// Order UUID di URL butuh faktor kedua (4 digit terakhir No. WA pemesan) supaya tidak
// jadi bearer token PII (fix A2 2026-08-23). Disimpan localStorage tepat setelah submit
// sukses (lihat actionsSubmit.ts) — pemesan asli di device yang sama lolos otomatis;
// link yang dibuka di device/browser lain butuh 4 digit itu.
const phoneLast4Key = (orderId: string) => `order_phone4_${orderId}`;

export function getStoredPhoneLast4(orderId: string): string | null {
  try { return localStorage.getItem(phoneLast4Key(orderId)); } catch { return null; }
}

export function storePhoneLast4(orderId: string, phone: string): void {
  try {
    const digits = (phone || '').replace(/\D/g, '');
    if (digits.length >= 4) localStorage.setItem(phoneLast4Key(orderId), digits.slice(-4));
  } catch { /* localStorage tidak tersedia (private mode dll) — abaikan */ }
}

// Faktor kedua via URL (?ph4=1234 di search ATAU hash) — link pembayaran WA
// dibuka di browser/device lain tanpa localStorage (insiden Rizki 00da5c76).
// Server (redirects.js paymentRedirect) menempelkannya; RPC memvalidasi сервер-side.
export function getPh4FromUrl(href?: string): string | null {
  try {
    const src = href ?? (typeof window !== 'undefined' ? window.location.search + ' ' + window.location.hash : '');
    const m = /[?&]ph4=(\d{4})(?!\d)/.exec(src);
    return m ? m[1] : null;
  } catch { return null; }
}

export function rememberPhoneLast4(orderId: string, last4: string | null): void {
  try {
    if (last4 && /^\d{4}$/.test(last4)) localStorage.setItem(phoneLast4Key(orderId), last4);
  } catch { /* abaikan */ }
}

/**
 * Fetch order dengan 3x retry (delay 1s, 2s)
 */
export function useFetchOrder(setOrder: React.Dispatch<React.SetStateAction<Order | null>>) {
  return useCallback(async (id: string, phoneLast4Override?: string): Promise<Order | null> => {
    const phoneLast4 = phoneLast4Override || getStoredPhoneLast4(id) || undefined;
    for (let attempt = 1; attempt <= 3; attempt++) {
      const result = await supabase.rpc('get_public_order_limited', { p_order_id: id, p_phone_last4: phoneLast4 }).maybeSingle();
      if (!result.error && result.data) {
        const loaded = result.data as Order;
        setOrder(loaded);
        return loaded;
      }
      if (attempt < 3) await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
    }
    return null;
  }, [setOrder]);
}
